import {FilterClause, ShowClause, ShowClause as ShowClauseType} from 'common/report/insights/util/types';
import {InsightsBookmarkParams, MetricType, SubBehavior} from 'common/types/reports/bookmark';

import {FilterClause as FilterClauseModel} from '../models/filter-clause';
import {isBehaviorShowClause, isFormulaShowClause} from '../models/utils';

export function hasValidBookmarkParams(params: InsightsBookmarkParams) {
  return hasValidMetricBookmarkShowClauses(params.sections.show) && hasValidFilters(params.sections.filter!);
}

/**
 * Checks if the provided array of show clauses contains valid metric bookmark parameters.
 *
 * This function validates each show clause in the array by performing the following checks:
 * - Ensures the input is a non-empty array.
 * - Validates each filter within the behavior of the clause.
 * - Validates the behavior of the clause, ensuring it has valid filters and either an id or name.
 * - For formula show clauses, ensures the clause has a name and definition, and recursively validates referenced metrics.
 * - For specific metric types (Funnel, Retention, RetentionFrequency), calls corresponding validation functions.
 * - For other cases, ensures the behavior has a name, id, or raw_cohort.
 *
 * @param {ShowClauseType[]} showClauses - The array of show clauses to validate.
 * @returns {boolean} - Returns true if all show clauses are valid, otherwise false.
 */
export function hasValidMetricBookmarkShowClauses(showClauses: ShowClauseType[]): boolean {
  if (!Array.isArray(showClauses) || showClauses.length === 0) {
    return false;
  }

  // Check if all clauses are valid
  return showClauses.every((clause) => {
    // Formula clause validation
    if (isFormulaShowClause(clause)) {
      return (
        Boolean(clause.id) ||
        // validate unsaved formulas
        (Boolean(clause && clause.name && clause.definition) &&
          (clause.referencedMetrics?.length ? hasValidMetricBookmarkShowClauses(clause.referencedMetrics) : true))
      );
    }

    // validate canonical metrics
    if (clause.id && `behavior` in clause === false) {
      return true;
    }

    // Different metric type validations
    if (clause.behavior!.type === MetricType.Simple) {
      return validateBehaviorClause(clause, {nestedLength: {atLeast: 1}});
    }

    if (clause.behavior!.type === MetricType.Funnel) {
      return validateBehaviorClause(clause, {nestedLength: {atLeast: 2}});
    }

    if (clause.behavior!.type === MetricType.Retention) {
      return validateBehaviorClause(clause, {nestedLength: {equal: 2}});
    }

    if (clause.behavior!.type === MetricType.RetentionFrequency) {
      return validateBehaviorClause(clause, {nestedLength: {equal: 1}});
    }

    // Default case - check if behavior has name, id, or raw_cohort
    return validateBehaviorClause(clause);
  });
}

/**
 * Validates a behavior clause based on specific criteria.
 *
 * @param {ShowClause} - The clause to validate
 * @param opts - Optional validation options.
 * @param opts.nestedLength - An object specifying the required number of nested behaviors.
 * @param opts.nestedLength.atLeast - If provided, specifies that the number of nested behaviors should be at least this number.
 * @param opts.nestedLength.equal - If provided, specifies that the number of nested behaviors should be exactly this number.
 * @returns `true` if the clause is a valid behavior clause and meets the specified criteria; otherwise, `false`.
 */
function validateBehaviorClause(
  clause: ShowClause | undefined | null,
  opts: {nestedLength?: {atLeast: number} | {equal: number}} = {},
): boolean {
  if (!isBehaviorShowClause(clause!)) {
    return false;
  }

  /** @todo(metrics) this is inaccurate, saved clauses could have modified data in them */
  if (clause.behavior?.id) {
    return true;
  }

  const behavior = clause.behavior!;
  const subBehaviors = behavior.behaviors ?? [];

  const validate = (subBehavior: SubBehavior) => {
    return hasValidFilters(subBehavior.filters) &&
      // funnel comparisons use deeply nested behaviors
      subBehavior.behaviors
      ? subBehavior.behaviors.every(validate)
      : Boolean(subBehavior.id || subBehavior.name);
  };

  let isValid = subBehaviors.length ? subBehaviors.every(validate) : validate(behavior as SubBehavior);

  // matches a certain number of nested behaviors
  if (isValid && opts.nestedLength) {
    if (`equal` in opts.nestedLength) {
      isValid = subBehaviors.length === opts.nestedLength.equal;
    } else if (`atLeast` in opts.nestedLength) {
      isValid = subBehaviors.length >= opts.nestedLength.atLeast;
    }
  }

  return isValid;
}

/**
 * Validate bookmark filters against `PropertyFilter` validation rules
 * @todo this should be rewritten to use only the bookmark fields
 */
function hasValidFilters(filters: FilterClause[] | null | undefined) {
  return filters
    ? filters.every((filter: FilterClause) => new FilterClauseModel(filter).toPropertyFilter().isValid())
    : true;
}
